import React, { useEffect, useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { Loader2 } from 'lucide-react';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { LanguageProvider } from '@/lib/i18n';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import OnboardingPage from '@/pages/OnboardingPage';
import AppsPage from '@/pages/AppsPage';
import SitesPage from '@/pages/website/SitesPage';
import SiteEditorPage from '@/pages/website/SiteEditorPage';
import SiteSettingsPage from '@/pages/website/SiteSettingsPage';
import SettingsPage from '@/pages/settings/SettingsPage';
import AdminPage from '@/pages/admin/AdminPage';
import PublicSitePage from '@/pages/PublicSitePage';
import ModulesPage from '@/pages/ModulesPage';
import ContactsPage from '@/pages/contacts/ContactsPage';
import DiscussPage from '@/pages/discuss/DiscussPage';
import DocumentsPage from '@/pages/documents/DocumentsPage';
import CrmPage from '@/pages/erp/CrmPage';
import SalesPage from '@/pages/erp/SalesPage';
import PurchasingPage from '@/pages/erp/PurchasingPage';
import PesanPage from '@/pages/pesan/PesanPage';
import ChatWidgetPage from '@/pages/pesan/ChatWidgetPage';
import InventoryPage from '@/pages/erp/InventoryPage';
import AssetsPage from '@/pages/erp/AssetsPage';
import WebmailPage from '@/pages/WebmailPage';
import PillotPayPage from '@/pages/erp/PillotPayPage';
import DashboardPage from '@/pages/erp/DashboardPage';
import MarketingPage from '@/pages/erp/MarketingPage';
import ProjectPage from '@/pages/project/ProjectPage';
import BoardPage from '@/pages/project/BoardPage';
import EcommercePage from '@/pages/erp/EcommercePage';
import PosPage from '@/pages/erp/PosPage';
import NotFoundPage from '@/pages/NotFoundPage';
import AccountingPage from '@/pages/erp/AccountingPage';
import HrPage from '@/pages/erp/HrPage';
import KioskPage from '@/pages/KioskPage';
import LandingPage from '@/pages/landing/LandingPage';
import { PricingPage, LibraryPage, TutorialPage, LegalPage } from '@/pages/landing/PublicPages';
import { ForgotPasswordPage, ResetPasswordPage } from '@/pages/PasswordResetPages';
import { api } from '@/lib/api';

const Splash = () => (
  <div className="min-h-screen flex items-center justify-center bg-apps-wash">
    <div className="flex flex-col items-center gap-3">
      <img src="/pillot-icon.png" alt="Pillot" className="h-12 w-12 animate-pulse" />
      <Loader2 className="animate-spin text-primary" size={20} />
    </div>
  </div>
);

const Protected = ({ children, needCompany = true }) => {
  const { user, loading, companies } = useAuth();
  const location = useLocation();
  if (loading) return <Splash />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  if (needCompany && companies.length === 0) return <Navigate to="/onboarding" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user, loading, companies } = useAuth();
  if (loading) return <Splash />;
  if (user) return <Navigate to={companies.length ? '/apps' : user.is_super_admin ? '/admin' : '/onboarding'} replace />;
  return children;
};

const SuperAdminOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (!user.is_super_admin) return <Navigate to="/apps" replace />;
  return children;
};

/** Detect custom-domain visits: if the host is not the app host, try to resolve a published site. */
const useCustomHostSite = () => {
  const [state, setState] = useState({ checked: false, site: null });
  useEffect(() => {
    const appHost = (() => {
      try {
        return new URL(process.env.REACT_APP_BACKEND_URL).host;
      } catch (e) {
        return window.location.host;
      }
    })();
    const host = window.location.host;
    const isAppHost = host === appHost || host.includes('localhost') || host.endsWith('.emergentagent.com') || host.endsWith('.emergent.host') || host.startsWith('127.');
    if (isAppHost) {
      setState({ checked: true, site: null });
      return;
    }
    api.get('/public/sites/by-host', { params: { host } })
      .then(({ data }) => setState({ checked: true, site: data }))
      .catch((e) => setState({ checked: true, site: e.response?.status === 401 && e.response.data?.protected ? e.response.data : null }));
  }, []);
  return state;
};

const AppRoutes = () => {
  const { checked, site } = useCustomHostSite();
  if (!checked) return <Splash />;
  if (site) {
    return (
      <Routes>
        <Route path="/blog/:postSlug" element={<PublicSitePage hostSite={site} />} />
        <Route path="/:pageSlug?" element={<PublicSitePage hostSite={site} />} />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/library" element={<LibraryPage />} />
      <Route path="/library/:slug" element={<TutorialPage />} />
      <Route path="/terms" element={<LegalPage kind="terms" />} />
      <Route path="/privacy" element={<LegalPage kind="privacy" />} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
      <Route path="/invite/:token" element={<RegisterPage inviteMode />} />
      <Route path="/onboarding" element={<Protected needCompany={false}><OnboardingPage /></Protected>} />
      <Route path="/apps" element={<Protected><AppsPage /></Protected>} />
      <Route path="/modules" element={<Protected><ModulesPage /></Protected>} />
      <Route path="/contacts/:contactId?" element={<Protected><ContactsPage /></Protected>} />
      <Route path="/discuss/:box?/:channelId?" element={<Protected><DiscussPage /></Protected>} />
      <Route path="/documents" element={<Protected><DocumentsPage /></Protected>} />
      <Route path="/crm" element={<Protected><CrmPage /></Protected>} />
      <Route path="/sales" element={<Protected><SalesPage /></Protected>} />
      <Route path="/purchasing" element={<Protected><PurchasingPage /></Protected>} />
      <Route path="/pesan/:tab?/:convId?" element={<Protected><PesanPage /></Protected>} />
      <Route path="/chat-widget/:key" element={<ChatWidgetPage />} />
      <Route path="/inventory" element={<Protected><InventoryPage /></Protected>} />
      <Route path="/assets" element={<Protected><AssetsPage /></Protected>} />
      <Route path="/webmail" element={<Protected><WebmailPage /></Protected>} />
      <Route path="/pillot-pay" element={<Protected><PillotPayPage /></Protected>} />
      <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
      <Route path="/marketing/:tab?/:campaignId?" element={<Protected><MarketingPage /></Protected>} />
      <Route path="/project" element={<Protected><ProjectPage /></Protected>} />
      <Route path="/project/:boardId" element={<Protected><BoardPage /></Protected>} />
      <Route path="/ecommerce" element={<Protected><EcommercePage /></Protected>} />
      <Route path="/pos" element={<Protected><PosPage /></Protected>} />
      <Route path="/accounting" element={<Protected><AccountingPage /></Protected>} />
      <Route path="/hr" element={<Protected><HrPage /></Protected>} />
      <Route path="/kiosk/:code" element={<KioskPage />} />
      <Route path="/website" element={<Protected><SitesPage /></Protected>} />
      <Route path="/website/:siteId/editor" element={<Protected><SiteEditorPage /></Protected>} />
      <Route path="/website/:siteId/settings/:tab?" element={<Protected><SiteSettingsPage /></Protected>} />
      <Route path="/settings/:tab?" element={<Protected><SettingsPage /></Protected>} />
      <Route path="/admin/:tab?" element={<SuperAdminOnly><AdminPage /></SuperAdminOnly>} />
      <Route path="/site/:slug/blog/:postSlug" element={<PublicSitePage />} />
      <Route path="/site/:slug/:pageSlug?" element={<PublicSitePage />} />
      <Route path="/store/*" element={<NotFoundPage />} />
      <Route path="*" element={<Navigate to="/apps" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
